/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import Flag from 'react-world-flags';
import Typography from '@mui/material/Typography';
import Ansi from '@cocalc/ansi-to-react';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router-dom';
import { api, type ServiceWithDates } from './api';
import { HabourMasterHeader } from './Logo';

const CustomWidthTooltip = styled(({ className, ...props }: any) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 800,
    maxHeight: '80vh',
    overflowY: 'auto',
  },
});

const SummaryLine: React.FC<{ emoji: string; item: any; total: number }> = ({ emoji, item, total }) => (
  <TableRow>
    <TableCell>
      <Typography fontSize="xx-large">{emoji}</Typography>
    </TableCell>
    <TableCell>
      <Typography fontSize="larger" fontWeight={600}>
        {item.count}
      </Typography>
    </TableCell>
    <TableCell>
      <Typography fontSize="larger">{Math.round((item.count * 100) / total)}%</Typography>
    </TableCell>
    <TableCell>{item.description}</TableCell>
    <TableCell sx={{ maxWidth: 300 }}>
      {Object.keys(item.countries)
        .map((key) => ({ key, value: item.countries[key] }))
        .sort((a, b) => b.value - a.value)
        .map(({ key, value }) => (
          <>
            <Chip label={value} size="small" icon={<Flag code={key} width={16} />} />{' '}
          </>
        ))}
    </TableCell>
  </TableRow>
);

export const Home: React.FC = () => {
  const [services, setServices] = React.useState<ServiceWithDates[]>([]);
  const [gateways, setGateways] = React.useState<any[]>([]);
  const [gatewayCount, setGatewayCount] = React.useState<number>();
  const [lastUpdated, setLastUpdated] = React.useState<Date>();
  const [busy, setBusy] = React.useState(false);
  const [busyMessage, setBusyMessage] = React.useState<string>();
  const [summary, setSummary] = React.useState<any>(null);

  const countCountries = (item: any) =>
    item
      .map((i: any) => i.explorer_pretty_bond.location.two_letter_iso_country_code)
      .reduce((acc: any, cur: any) => {
        if (!acc[cur]) {
          acc[cur] = 1;
        } else {
          acc[cur] += 1;
        }
        return acc;
      }, {});

  React.useEffect(() => {
    if (gateways) {
      const pandas = gateways.filter((g) => g.config_score_calculated >= 300);
      const dolphins = gateways.filter((g) => g.config_score_calculated <= 200 && g.config_score_calculated > 100);
      const foxes = gateways.filter((g) => g.config_score_calculated <= 100);
      const mouses = gateways.filter((g) => g.config_score_calculated === 0);

      const peanuts = gateways.filter((g) => g.probe_score_calculated >= 700);
      const icecreams = gateways.filter((g) => g.probe_score_calculated <= 600 && g.probe_score_calculated > 500);
      const popcorns = gateways.filter((g) => g.probe_score_calculated <= 500 && g.probe_score_calculated > 200);
      const lollies = gateways.filter((g) => g.probe_score_calculated <= 200);

      setSummary({
        configScores: {
          pandas: {
            count: pandas.length,
            countries: countCountries(pandas),
            description: (
              <>
                ✅ Entry Gateway
                <br />✅ Exit Gateway with IPR
                <br />✅ Exit Gateway with SOCKS5 NR
                <br />✅ Exit Policy Enabled
              </>
            ),
          },
          dolphins: {
            count: dolphins.length,
            countries: countCountries(dolphins),
            description: (
              <>
                ✅ Entry Gateway
                <br />✅ Exit Gateway with SOCKS5 NR
                <br />✅ Exit Policy Enabled
              </>
            ),
          },
          foxes: {
            count: foxes.length,
            countries: countCountries(foxes),
            description: (
              <>
                ✅ Entry Gateway
                <br />✅ Exit Gateway with SOCKS5 NR
                <br />❌ Exit Policy Not Enabled
              </>
            ),
          },
          mouses: {
            count: mouses.length,
            countries: countCountries(mouses),
            description: <>✅ Entry Gateway</>,
          },
        },
        routingScores: {
          peanuts: {
            count: peanuts.length,
            countries: countCountries(peanuts),
            description: (
              <>
                ↗️ Entry Gateway Connects
                <br />
                🔀 Entry Gateway Routes
                <br />
                ➡️ Exit Gateway Connects
                <br />
                <span style={{ opacity: 0.5 }}>4️⃣️6️⃣</span> Exit Gateway ipv4 and ipv6 routes internally
                <br />
                <span>4️⃣️6️⃣</span> Exit Gateway ipv4 and ipv6 routes externally
              </>
            ),
          },
          icecreams: {
            count: icecreams.length,
            countries: countCountries(icecreams),
            description: (
              <>
                ↗️ Entry Gateway Connects
                <br />
                🔀 Entry Gateway Routes
                <br />
                ➡️ Exit Gateway Connects
                <br />
                <span style={{ opacity: 0.5 }}>4️⃣️6️⃣</span> Exit Gateway ipv4 and ipv6 routes internally
                <br />❌ Exit Gateway not routing externally
              </>
            ),
          },
          popcorns: {
            count: popcorns.length,
            countries: countCountries(popcorns),
            description: (
              <>
                ↗️ Entry Gateway Connects
                <br />
                🔀 Entry Gateway Routes
                <br />
                ➡️ Exit Gateway Connects
                <br />❌ Exit Gateway does not route ipv4 or ipv6
              </>
            ),
          },
          lollies: {
            count: lollies.length,
            countries: countCountries(lollies),
            description: (
              <>
                ↗️ Entry Gateway Connects
                <br />
                🔀 Entry Gateway Routes
                <br />❌ Exit Gateway does not connect
              </>
            ),
          },
        },
      });
    }
  }, [gateways]);

  const refreshServices = async () => {
    try {
      const newServices = await api.services();
      setServices(newServices.items);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error getting services', e);
    }
  };

  const refreshGateways = async () => {
    try {
      const newGateways = await api.gateways();
      window.sessionStorage.setItem('gateways', JSON.stringify(newGateways.items));
      setGateways(newGateways.items);
      setGatewayCount(newGateways.total);
      setBusyMessage('Gateway data updated...');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error getting services', e);
    }
  };

  const refresh = async () => {
    try {
      setBusy(true);
      setBusyMessage('Getting latest data...');
      await Promise.all([refreshServices(), refreshGateways()]);
      setLastUpdated(new Date());
      setBusyMessage('Finishing refresh...');
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    const storedGateways = window.sessionStorage.getItem('gateways');
    if (storedGateways?.length) {
      try {
        setGateways(JSON.parse(storedGateways));
        setLastUpdated(new Date());
      } catch (_) {
        // do nothing
      }
    }

    refresh();
    const timer = setInterval(refresh, 1000 * 60 * 5);
    return () => clearInterval(timer);
  }, []);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: 'Identity Key',
        accessorKey: 'gateway_identity_key',
        Cell: ({ cell }) => (
          <Link component={RouterLink} to={`/gateway/${cell.getValue<string>()}`}>
            {cell.getValue<string>()}
          </Link>
        ),
      },
      {
        header: 'Config Score',
        accessorKey: 'config_score_calculated',
        id: 'configScore',
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();
          if (value >= 300) {
            return <>🐼</>;
          }
          if (value >= 200) {
            return <>🐬</>;
          }
          if (value >= 100) {
            return <>🦊</>;
          }
          return <>❌</>;
        },
      },
      {
        header: 'Probe Score',
        accessorKey: 'probe_score_calculated',
        id: 'probeScoreCalculated',
        Cell: ({ cell }) => {
          const value = cell.getValue<number>();

          if (value >= 700) {
            return <Typography fontSize="large">🥜</Typography>;
          }
          if (value >= 600) {
            return <Typography fontSize="large">🍦</Typography>;
          }
          if (value >= 400) {
            return <Typography fontSize="large">🍿</Typography>;
          }
          if (value >= 200) {
            return <Typography fontSize="large">🍭</Typography>;
          }
          return <>❌</>;
        },
      },
      {
        header: 'Location',
        accessorKey: 'explorer_pretty_bond.location.two_letter_iso_country_code',
        Cell: ({ cell }) => <Flag code={cell.getValue<string>()} width="20" />,
      },
      {
        header: 'Probe result',
        accessorKey: 'last_probe_result',
        Cell: ({ cell }) => {
          const value = cell.getValue<any>();
          const logLines = cell.row.original?.last_probe_log || '';

          const badges = [];
          if (value?.outcome?.as_entry?.can_connect) {
            badges.push(<>↗️</>);
          }
          if (value?.outcome?.as_entry?.can_route) {
            badges.push(<>🔀</>);
          }
          if (value?.outcome?.as_exit?.can_connect) {
            badges.push(<>➡️</>);
          }
          if (value?.outcome?.as_exit?.can_route_ip_v4) {
            badges.push(<span style={{ opacity: 0.5 }}>4️⃣</span>);
          }
          if (value?.outcome?.as_exit?.can_route_ip_v6) {
            badges.push(<span style={{ opacity: 0.5 }}>6️⃣</span>);
          }
          if (value?.outcome?.as_exit?.can_route_ip_external_v4) {
            badges.push(<>4️⃣</>);
          }
          if (value?.outcome?.as_exit?.can_route_ip_external_v6) {
            badges.push(<>6️⃣</>);
          }

          return (
            <CustomWidthTooltip
              placement="left"
              title={
                <Box py={0} my={0} component="pre">
                  <pre>
                    <Ansi>{logLines}</Ansi>
                  </pre>
                </Box>
              }
            >
              <Tooltip placement="right" title={<pre>{JSON.stringify(value, null, 2)}</pre>}>
                <span>{badges}</span>
              </Tooltip>
            </CustomWidthTooltip>
          );
        },
      },
      {
        header: 'Exit Gateway has IPR?',
        accessorKey: 'self_described.ip_packet_router.address',
        Cell: ({ cell }) => (cell.getValue() ? '✅' : null),
      },
      {
        header: 'Exit Gateway has NR',
        accessorKey: 'self_described.network_requester.address',
        Cell: ({ cell }) => (cell.getValue() ? '✅' : null),
      },
      {
        header: 'NR (uses exit policy)',
        accessorKey: 'self_described.network_requester.uses_exit_policy',
        Cell: ({ cell }) => (cell.getValue() ? '✅' : null),
      },
      {
        header: 'Software Version',
        accessorKey: 'self_described.build_information.build_version',
      },
      {
        header: 'Git Commit Hash',
        accessorKey: 'self_described.build_information.commit_sha',
        Cell: ({ cell }) => <code>{cell.getValue<string>()?.slice(0, 6)}</code>,
      },
      // {
      //   header: 'Name',
      //   accessorKey: 'description.name',
      // }, {
      //   header: 'Description',
      //   accessorKey: 'description.description',
      // }
    ],
    [],
  );
  const table = useMaterialReactTable({
    columns,
    initialState: {
      pagination: { pageSize: 25, pageIndex: 0 },
      density: 'compact',
      showColumnFilters: true,
      sorting: [
        {
          id: 'probeScoreCalculated',
          desc: true,
        },
      ],
    },
    data: gateways,
    enableRowSelection: true, // enable some features
    enableColumnOrdering: false, // enable a feature for all columns
    enableGlobalFilter: false, // turn off a feature
  });

  if (!lastUpdated) {
    return (
      <Box p={4}>
        <HabourMasterHeader />
        <Box mt={4} display="flex" alignItems="center">
          <CircularProgress />
          <Typography fontSize="larger" ml={2}>
            Please wait while gathering rainbows and sorting kittens... 🌈😼
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <HabourMasterHeader />

      <h2>Gateways{gatewayCount && <span>{` (${gatewayCount})`}</span>}</h2>

      {summary && gateways && (
        <Box mt={2} pb={2}>
          <Stack direction="row" spacing={4}>
            <Box>
              <Typography fontWeight={600}>Config scores</Typography>
              <Table>
                <TableBody>
                  <SummaryLine emoji="🐼" item={summary.configScores.pandas} total={gateways.length} />
                  <SummaryLine emoji="🐬" item={summary.configScores.dolphins} total={gateways.length} />
                  <SummaryLine emoji="🦊" item={summary.configScores.foxes} total={gateways.length} />
                </TableBody>
              </Table>
            </Box>
            <Box>
              <Typography fontWeight={600}>Routing scores</Typography>
              <Table>
                <TableBody>
                  <SummaryLine emoji="🥜" item={summary.routingScores.peanuts} total={gateways.length} />
                  <SummaryLine emoji="🍦" item={summary.routingScores.icecreams} total={gateways.length} />
                  <SummaryLine emoji="🍿" item={summary.routingScores.popcorns} total={gateways.length} />
                  <SummaryLine emoji="🍭" item={summary.routingScores.lollies} total={gateways.length} />
                </TableBody>
              </Table>
            </Box>
          </Stack>
        </Box>
      )}

      <Box mt={5} py={2}>
        <Box display="flex" justifyContent="space-between">
          <Box>
            {busy ? (
              <Typography>{busyMessage || 'Refreshing...'}</Typography>
            ) : (
              <Button variant="outlined" onClick={refresh}>
                Refresh
              </Button>
            )}
          </Box>
          {lastUpdated && (
            <Stack direction="row" spacing={2}>
              <Typography>Last updated</Typography>
              <Typography>
                <strong>
                  {formatDistanceToNow(lastUpdated, {
                    addSuffix: true,
                  })}
                </strong>
                <br />
                {lastUpdated.toISOString()}
              </Typography>
            </Stack>
          )}
        </Box>
        <MaterialReactTable table={table} />
      </Box>

      <h2>SOCKS5 Network Requesters</h2>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Network Requester Client Id</TableCell>
              <TableCell>Gateway Identity Key</TableCell>
              <TableCell>Routing score</TableCell>
              <TableCell>Last successful ping</TableCell>
              <TableCell>Created on</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services
              .sort((a, b) => a.gateway_identity_key.localeCompare(b.gateway_identity_key))
              .map((service) => (
                <TableRow key={service.gateway_identity_key}>
                  <TableCell sx={{ maxWidth: '15vw' }}>
                    <Box sx={{ overflowX: 'auto' }}>{service.service_provider_client_id}</Box>
                  </TableCell>
                  <TableCell sx={{ maxWidth: '15vw' }}>
                    <Box sx={{ overflowX: 'auto' }}>{service.gateway_identity_key}</Box>
                  </TableCell>
                  <TableCell>{Math.round(service.routing_score * 100)}%</TableCell>
                  <TableCell>
                    <strong>
                      {formatDistanceToNow(service.last_successful_ping_utc, {
                        addSuffix: true,
                      })}
                    </strong>
                    <br />
                    {service.last_successful_ping_utc.toISOString()}
                  </TableCell>
                  <TableCell>
                    <strong>
                      {formatDistanceToNow(service.last_updated_utc, {
                        addSuffix: true,
                      })}
                    </strong>
                    <br />
                    {service.last_updated_utc.toISOString()}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
